import NetworkErrorModal from '@/common/components/modals/NetworkErrorModal.tsx';
import RequestErrorModal from '@/common/components/modals/RequestErrorModal.tsx';
import useModal from '@/common/hooks/useModal.ts';
import useToast from '@/common/store/alert/useToast.ts';
import useAuthStorage from '@/common/store/authStorage/useAuthStorage';
import { ApiErrorResponse } from '@/common/types/ApiResponseTypes.type.ts';
import {
  MutationCache,
  Query,
  QueryCache,
  QueryClient,
  QueryClientProvider,
  QueryKey,
} from '@tanstack/react-query';
import { ReactNode, useRef, useState } from 'react';

type QueryObjectErrorType = {
  show: true;
  duration: number;
};

type QueryObjectSuccessType = {
  message: string;
  duration: number;
};

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: ApiErrorResponse;
    mutationMeta: {
      error?: QueryObjectErrorType | false;
      successToast?: QueryObjectSuccessType | string;
    };
    queryMeta: {
      onSuccess?: (data: any) => void;
      onError?: (err: any) => void;
    };
  }
}

//todo it should be refactored !
const TanStackQueryProvider = ({ children }: { children: ReactNode }) => {
  const showAlert = useToast((state) => state.showAlert);
  const errorQueries = useRef<Query<unknown, unknown, unknown, QueryKey>[]>([]);
  const { removeToken } = useAuthStorage();
  const {
    open: isNetworkErrorModalOpen,
    // openModal: openNetworkErrorModal,
    forceCloseModal: forceCloseNetworkErrorModal,
  } = useModal({
    closeModalLock: true,
  });
  const {
    open: isRequestErrorModalOpen,
    // openModal: openRequestErrorModal,
    forceCloseModal: forceCloseRequestErrorModal,
  } = useModal({
    closeModalLock: true,
  });
  const [queryClient] = useState(
    new QueryClient({
      mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) => {
          const mutationMeta = mutation.meta;
          // if the error is undefined we will show the error by default
          if (error.fa_details && mutationMeta?.error !== false) {
            showAlert({
              description: error.fa_details,
              type: 'error',
              duration: mutationMeta?.error
                ? mutationMeta.error.duration
                : 4000,
            });
          }
        },
        onSuccess: (_data, _variables, _context, mutation) => {
          const mutationMeta = mutation.meta;
          if (mutationMeta?.successToast) {
            showAlert({
              description:
                typeof mutationMeta?.successToast === 'string'
                  ? mutationMeta.successToast
                  : mutationMeta.successToast.message,
              type: 'success',
              duration:
                typeof mutationMeta?.successToast === 'string'
                  ? 3000
                  : mutationMeta.successToast.duration,
            });
          }
        },
      }),
      queryCache: new QueryCache({
        onError: (error, _query) => {
          const queryMeta = _query.meta;
          if (queryMeta?.onError) {
            queryMeta.onError(error);
          }
          if (error.statusCode === 401) {
            removeToken();
          }
        },
        onSuccess: (data, query) => {
          // Call the onSuccess handler from meta if it exists
          const queryMeta = query.meta;
          if (queryMeta?.onSuccess) {
            queryMeta.onSuccess(data);
          }
        },
      }),
      defaultOptions: {
        queries: {
          retry: (failureCount, error) =>
            error.statusCode >= 500 && failureCount < 4,
          gcTime: 0,
          staleTime: 0,
          refetchOnMount: false,
          refetchOnWindowFocus: false,
        },
      },
    }),
  );

  const retryNetworkErrorFunction = () => {
    forceCloseNetworkErrorModal();
    errorQueries.current.forEach((query) => {
      query.fetch();
    });
    errorQueries.current = [];
  };

  const retryRequestErrorFunction = () => {
    forceCloseRequestErrorModal();
    errorQueries.current.forEach((query) => {
      query.fetch();
    });
    errorQueries.current = [];
  };
  return (
    <QueryClientProvider client={queryClient}>
      {/*<ReactQueryDevtools*/}
      {/*  initialIsOpen={false}*/}
      {/*  buttonPosition="top-left"*/}
      {/*  position="left"*/}
      {/*/>*/}
      <NetworkErrorModal
        closeModal={() => {}}
        open={isNetworkErrorModalOpen}
        retryFunction={retryNetworkErrorFunction}
      />
      <RequestErrorModal
        open={isRequestErrorModalOpen}
        closeModal={() => {}}
        retryFunction={retryRequestErrorFunction}
      />
      {children}
    </QueryClientProvider>
  );
};
export default TanStackQueryProvider;
